import React, { useState } from 'react';
import { Grid, Typography, CircularProgress } from '@material-ui/core';
import Input from '../../TextInput';
import { getCurrencySymbol } from '../utils';
import DeliverySummary from './DeliverySummary';
import ExcelDownloadButton from './ExcelDownloadButton';
import AccountUpdateModal from './AccountUpdateModal';

const CURRENCIES = ['USD', 'EUR', 'LIB'];

const SectionHeader = ({ title, showEdit, onEdit, style }) => (
  <Grid
    container
    justifyContent="space-between"
    alignItems="center"
    style={{
      paddingBottom: 12,
      borderBottom: '1px solid #000',
      marginBottom: 14,
      ...style,
    }}
  >
    <Typography style={{ fontSize: 18 }}>{title}</Typography>
    {showEdit && (
      <Grid onClick={onEdit}>
        <Typography align="right" style={{ cursor: 'pointer', fontSize: 12 }}>
          Edit
        </Typography>
      </Grid>
    )}
  </Grid>
);

const InfoLine = ({ children }) => (
  <Typography style={{ fontSize: 12, lineHeight: 2 }}>{children}</Typography>
);

export default function SummaryPanel({
  isMobile,
  wid,
  show,
  nav,
  currency,
  setCurrency,
  groups,
  summaries,
  user,
  shipAdd,
  selectedShipAddress,
  orderSelected,
  downloadExcelURL,
  loadingOrder,
  onGenerateExcel,
  seasonName,
  onSubmitReview,
  onEditShipping,
  onRefreshUser,
}) {
  const [expanded, setExpanded] = useState([]);
  const [comment, setComment] = useState('');
  const [openUpdateModal, setOpenUpdateModal] = useState(false);
  const [isPayable, setIsPayable] = useState(false);

  if (isMobile && !show) return null;

  const canEdit = !orderSelected || orderSelected.status === 'Notes';
  const symbol = getCurrencySymbol(currency);
  const shippingAddress = shipAdd[selectedShipAddress];

  const togglePanel = panel => () => {
    setExpanded(prev =>
      prev.includes(panel)
        ? prev.filter(item => item !== panel)
        : [...prev, panel]
    );
  };

  const handleOpenUpdate = payable => {
    setIsPayable(payable);
    setOpenUpdateModal(true);
  };

  const totalStyles =
    groups.d1.length + groups.d2.length + groups.d3.length + groups.d4.length;
  const totalAmount =
    summaries.d1.total +
    summaries.d2.total +
    summaries.d3.total +
    summaries.d4.total;

  const excelButton = style => (
    <ExcelDownloadButton
      downloadExcelURL={downloadExcelURL}
      loading={loadingOrder}
      onGenerate={onGenerateExcel}
      seasonName={seasonName}
      style={style}
    />
  );

  const deliveries = [
    { key: 'd1', title: 'Delivery 1', expandable: true },
    { key: 'd2', title: 'Delivery 2', expandable: true },
    { key: 'd3', title: 'Delivery 3', expandable: true },
    { key: 'd4', title: 'Ski Capsule', expandable: false },
  ];

  return (
    <Grid
      style={
        isMobile
          ? {
              position: 'fixed',
              right: '2%',
              bottom: 55,
              maxHeight: 550,
              zIndex: 5000,
              background: '#fff',
            }
          : {
              position: 'fixed',
              right: '2%',
              top: 250,
              maxHeight: 550,
            }
      }
    >
      <Grid
        style={{
          width: isMobile ? wid * 0.96 : wid * 0.2,
          border: '2px solid #000000',
          padding: '21px 15px 17px',
        }}
      >
        <AccountUpdateModal
          open={openUpdateModal}
          onClose={() => setOpenUpdateModal(false)}
          isPayable={isPayable}
          user={user}
          canClose={canEdit}
          onSaved={() => {
            setOpenUpdateModal(false);
            onRefreshUser();
          }}
        />
        {nav === 'place' ? (
          <>
            {isMobile &&
              excelButton({
                bottom: 55,
                width: wid,
                paddingRight: 30,
              })}
            {window.innerHeight <= 750 &&
              !isMobile &&
              excelButton({ marginLeft: 20, top: -55, right: 23 })}
            <Grid container justifyContent="space-between">
              <Typography style={{ fontSize: 18 }}>Order Summary</Typography>
              <Grid>
                {CURRENCIES.map(c => (
                  <Typography
                    key={c}
                    display="inline"
                    onClick={() => setCurrency(c)}
                    style={{
                      paddingBottom: 3,
                      marginRight: 20,
                      borderBottom: currency === c && '2px solid #000000',
                      cursor: 'pointer',
                      fontSize: 12,
                      fontWeight: currency === c && 'bold',
                    }}
                  >
                    {c}
                  </Typography>
                ))}
              </Grid>
            </Grid>
            <Grid
              style={{
                borderTop: '1px solid #000000',
                borderBottom: '1px solid #000000',
                marginTop: 18,
              }}
            >
              {deliveries.map(({ key, title, expandable }) =>
                groups[key].length > 0 ? (
                  <DeliverySummary
                    key={key}
                    title={title}
                    count={groups[key].length}
                    summary={summaries[key]}
                    currency={currency}
                    expanded={expanded.includes(key)}
                    onToggle={togglePanel(key)}
                    panelId={`panel-${key}`}
                    expandable={expandable}
                  />
                ) : null
              )}
            </Grid>
            <Grid
              container
              justifyContent="space-between"
              style={{ marginTop: 15 }}
            >
              <Typography style={{ fontSize: 14, fontWeight: 'bold' }}>
                Total
              </Typography>
              <Typography style={{ fontSize: 14 }}>
                {totalStyles} styles
              </Typography>
              <Typography style={{ fontSize: 16, fontWeight: 'bold' }}>
                <span>{symbol}</span> {totalAmount}
              </Typography>
            </Grid>
            {window.innerHeight > 750 &&
              !isMobile &&
              excelButton({ marginLeft: 20, top: 55, right: 23 })}
          </>
        ) : (
          nav === 'review' && (
            <Grid style={{ maxHeight: 500, overflow: 'auto' }}>
              <SectionHeader
                title="Account Information"
                showEdit={canEdit}
                onEdit={() => handleOpenUpdate(false)}
              />
              <InfoLine>{user && user.shopName}</InfoLine>
              <InfoLine>{user && user.name}</InfoLine>
              <InfoLine>{user && user.email}</InfoLine>
              <InfoLine>{user && user.phone}</InfoLine>
              <SectionHeader
                title="Shipping Address"
                showEdit={canEdit}
                onEdit={onEditShipping}
                style={{ marginTop: 17 }}
              />
              <InfoLine>{shippingAddress && shippingAddress.name}</InfoLine>
              <InfoLine>
                {shippingAddress && shippingAddress.street}
                {' - '}
                {shippingAddress && shippingAddress.city}
              </InfoLine>
              <InfoLine>
                {shippingAddress && shippingAddress.zipcode}
                {' - '}
                {shippingAddress && shippingAddress.country}
              </InfoLine>
              <SectionHeader
                title="Billing Address"
                showEdit={canEdit}
                onEdit={onEditShipping}
                style={{ marginTop: 17 }}
              />
              <InfoLine>{user && user.bAdress1}</InfoLine>
              <InfoLine>
                {user && user.bAdress2}
                {' - '}
                {user && user.bAdress3}
              </InfoLine>
              <InfoLine>
                {user && user.bZipcode}
                {' - '}
                {user && user.bCountry}
              </InfoLine>
              {canEdit && (
                <SectionHeader
                  title="Accounts Payable"
                  showEdit={canEdit}
                  onEdit={() => handleOpenUpdate(true)}
                  style={{ marginTop: 17 }}
                />
              )}
              <InfoLine>{user && user.payName}</InfoLine>
              <InfoLine>{user && user.payEmail}</InfoLine>
              <InfoLine>{user && user.payPhone}</InfoLine>
              <Input
                field="Add comment"
                width={isMobile ? wid * 0.85 : wid * 0.18}
                height={127}
                marginTop={20}
                value={comment}
                onChange={event => setComment(event.target.value)}
                multiline
                comment
                rows={4}
              />
            </Grid>
          )
        )}
        {nav === 'review' && !isMobile && (
          <Grid
            container
            justifyContent="center"
            alignItems="center"
            style={{
              height: 40,
              width: wid * 0.18,
              marginTop: 22,
              background: canEdit ? '#000' : '#d3d3d3',
              cursor: canEdit ? 'pointer' : undefined,
            }}
            onClick={onSubmitReview}
          >
            <Typography style={{ fontSize: 14, color: '#fff' }}>
              {loadingOrder ? (
                <CircularProgress size={20} style={{ color: '#fafafa' }} />
              ) : (
                'Submit Order'
              )}
            </Typography>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
}
